import React, { useContext, useEffect, useState } from 'react';
import UseBuilderActions from '../../../builder/useBuilderActions';
import ButtonWithIndicator from '../../../../components/common/ButtonWithIndicator';
import { useTrackingUsers } from '../../../session/useTrackingUsers';
import { publishStartHandler } from '../../../data/actions';
import { DispatchContext, StateContext } from '../../../../App';

const groups = [
  {
    title: 'Update',
    items: [
      { action: 'update', project: 'all', title: 'Update customer', description: 'Applies the current configuration to the customer setup on the server.', icon: 'far fa-sync', button: 'Update' },
      { action: 'release', project: 'resolve', title: 'Resolve data and vehicles', description: 'Resolves all website data and vehicles again.', icon: 'far fa-car', button: 'Resolve' }
    ]
  },
  {
    title: 'Release',
    items: [
      { action: 'release', project: 'website', title: 'Release website', description: 'Builds and deploys the public website.', icon: 'far fa-globe', button: 'Release' },
      { action: 'release', project: 'cms', title: 'Release CMS', description: 'Deploys the CMS and runs its database migrations.', icon: 'far fa-server', button: 'Release', serverOnly: true },
      { action: 'release', project: 'builder', title: 'Release builder', description: 'Builds and deploys the website builder.', icon: 'far fa-cubes', button: 'Release' },
      { action: 'release', project: 'all', title: 'Release all', description: 'Runs the full release for this customer.', icon: 'far fa-rocket', button: 'Release', serverOnly: true }
    ]
  }
];

const itemKey = (item) => `${item.action}-${item.project}`;

const formatDuration = (ms) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const lineClass = (line) => {
  const text = line.toLowerCase();
  if (text.includes('failed') || text.includes('something wrong')) return 'builder-text-red-300';
  if (text.includes('success') || text.includes('published!') || text.includes('completed') || text.includes('done!')) return 'builder-text-green-300';
  return '';
};

const Elapsed = ({ from }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  return formatDuration(now - from);
};

const ScriptOutput = ({ output, run, onClose }) => {
  const lines = (output || '')
    .split('\n')
    .map((line) => line.replace(/^\s*--\s*/, '').trim())
    .filter(Boolean);

  return (
    <div className='builder-mt-4 builder-ml-14 builder-overflow-hidden builder-rounded-md builder-bg-primary builder-text-slate-100'>
      <div className='builder-flex builder-items-center builder-justify-between builder-px-3 builder-py-2 builder-border-b builder-border-white/10 builder-text-slate-300'>
        <span>Output</span>
        {run.finishedAt && (
          <button type='button' onClick={onClose} className='builder-text-slate-300 hover:builder-text-white'>
            Hide
          </button>
        )}
      </div>
      <div style={{ maxHeight: 240, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} className='builder-flex builder-flex-col-reverse builder-overflow-auto builder-px-3 builder-py-2 builder-text-[13px] builder-leading-relaxed'>
        <div>
          {lines.length === 0 && !run.finishedAt && <div className='builder-text-slate-400'>Waiting for output…</div>}
          {lines.map((line, i) => (
            <div key={i} className={lineClass(line)}>
              {line}
            </div>
          ))}
          {run.finishedAt && <div className='builder-text-slate-400'>Script finished after {formatDuration(run.finishedAt - run.startedAt)}</div>}
        </div>
      </div>
    </div>
  );
};

const ActionRow = ({ item, run, isReleasing, output, companyCode, onRun, onClose }) => {
  const isCurrent = run?.key === itemKey(item);
  const running = isCurrent && !run.finishedAt;

  return (
    <div className={`builder-p-4 ${isCurrent ? 'builder-bg-slate-50' : 'builder-bg-white'}`}>
      <div className='builder-flex builder-items-center builder-gap-4'>
        <div
          className={`builder-flex builder-flex-shrink-0 builder-items-center builder-justify-center builder-w-10 builder-h-10 builder-rounded-md ${isCurrent ? 'builder-bg-primary builder-text-white' : 'builder-bg-slate-100 builder-text-primary'} ${
            item.serverOnly ? 'builder-opacity-50' : ''
          }`}
        >
          <i className={item.icon} />
        </div>
        <div className='builder-flex-1 builder-min-w-0'>
          <div className='builder-font-semibold builder-text-md builder-text-slate-800'>{item.title}</div>
          <div className='builder-text-slate-500'>{item.description}</div>
          {item.serverOnly && (
            <div className='builder-flex builder-flex-wrap builder-items-center builder-gap-x-2 builder-mt-2 builder-text-slate-500'>
              <i className='far fa-lock' />
              Run this on the server:
              <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} className='builder-px-1.5 builder-py-0.5 builder-rounded builder-bg-slate-100 builder-text-slate-800 builder-text-[13px]'>
                ./release_customer.sh {companyCode} {item.project}
              </code>
            </div>
          )}
        </div>
        {running ? (
          <div className='builder-flex builder-flex-shrink-0 builder-items-center builder-font-semibold builder-text-primary'>
            <i className='far fa-spinner-third fa-spin builder-mr-2' />
            Running for <Elapsed from={run.startedAt} />
          </div>
        ) : (
          <ButtonWithIndicator
            text={item.button}
            disabled={isReleasing || item.serverOnly}
            onClick={() => onRun(item)}
            className='builder-flex-shrink-0 builder-justify-center builder-min-w-[104px] builder-rounded-md'
            colorClass='builder-bg-white builder-text-primary hover:builder-bg-primary hover:builder-text-white'
            borderClass='builder-border builder-border-slate-300 hover:builder-border-primary'
          />
        )}
      </div>
      {isCurrent && <ScriptOutput output={output} run={run} onClose={onClose} />}
    </div>
  );
};

const Release = () => {
  const state = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const { executeScript } = UseBuilderActions();
  const { isReleasing, userPublished } = useTrackingUsers();
  const [run, setRun] = useState(null);

  useEffect(() => {
    if (!isReleasing) setRun((current) => (current && !current.finishedAt ? { ...current, finishedAt: Date.now() } : current));
  }, [isReleasing]);

  const handleRun = (item) => {
    if (isReleasing || item.serverOnly) return;
    setRun({ key: itemKey(item), startedAt: Date.now() });
    publishStartHandler(dispatch);
    executeScript(item.action, item.project);
    userPublished();
  };

  const otherScriptRunning = isReleasing && (!run || run.finishedAt);

  return (
    <div className='builder-flex builder-flex-col builder-gap-8 builder-max-w-4xl builder-pr-4 builder-pb-6'>
      <p className='builder-text-slate-500'>
        Run update and release scripts for <span className='builder-font-semibold builder-text-slate-800'>{state.configuration?.company_code}</span>. Only one script can run at a time.
      </p>

      {otherScriptRunning && (
        <div className='builder-flex builder-items-center builder-gap-2 builder-p-3 builder-rounded-md builder-border builder-border-amber-200 builder-bg-amber-50 builder-text-amber-800'>
          <i className='far fa-lock' />
          Another script is running. You can start a new one when it finishes.
        </div>
      )}

      {groups.map((group) => (
        <section key={group.title}>
          <h2 className='builder-mb-2 builder-font-semibold builder-text-md builder-text-slate-800'>{group.title}</h2>
          <div className='builder-overflow-hidden builder-border builder-border-slate-200 builder-rounded-lg builder-divide-y builder-divide-slate-200'>
            {group.items.map((item) => (
              <ActionRow key={itemKey(item)} item={item} run={run} isReleasing={isReleasing} output={state.publish.output} companyCode={state.configuration?.company_code} onRun={handleRun} onClose={() => setRun(null)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Release;
